import { NOTIFICATION_EVENT_LABELS } from '../notifications/types';
import type { NotificationEvent } from '../notifications/types';

/**
 * The rules the Settings screen runs on.
 *
 * None of this enforces anything — every one of these is re-decided in the
 * database, by RLS on user_notification_preferences and workspace_settings and
 * by slack_fanout_allowed(). What lives here is the part that keeps the screen
 * honest about what a control actually does, which matters more than usual
 * because the whole page is controls whose effects are invisible.
 */

export interface NotificationPreference {
  event_type: NotificationEvent;
  slack_enabled: boolean;
  in_app_alert_enabled: boolean;
}

/**
 * The events worth offering a toggle for.
 *
 * Not every value in the enum. Three of them — the due-date reminders and the
 * overdue nudge — come from a scheduled job rather than from a person, and two
 * are supplier-portal events most of the company never sees. Listing all twelve
 * would make the useful four hard to find.
 *
 * Order is the order they appear, and it is deliberate: the ones that fire
 * most often are first, because those are the ones somebody came here to turn
 * off.
 */
export const PREFERENCE_EVENTS: NotificationEvent[] = [
  'mentioned',
  'task_assigned',
  'collaborator_added',
  'added_to_project',
  'due_date_changed',
  'moved_to_for_review',
  'task_due_tomorrow',
  'task_overdue',
  'revision_requested',
  'file_submitted_for_review',
];

/**
 * How a toggle reads on the page.
 *
 * NOTIFICATION_EVENT_LABELS are written for a sentence in the Inbox — "Ana
 * mentioned you" — and read badly as a row label: "mentioned you" with a
 * switch beside it is describing somebody else's action rather than a setting.
 */
const SETTING_LABELS: Partial<Record<NotificationEvent, string>> = {
  mentioned: 'Someone @mentions me',
  // Huddle comments are deliberately Inbox-only and therefore are not in
  // PREFERENCE_EVENTS, whose rows include a Slack switch.
  huddle_comment: 'Alex comments through my huddle link',
  task_assigned: 'A task is assigned to me',
  collaborator_added: 'I am added to a task',
  added_to_project: 'I am added to a project',
  due_date_changed: 'A due date changes',
  moved_to_for_review: 'A task moves to For Review',
  task_due_tomorrow: 'A task of mine is due tomorrow',
  task_overdue: 'A task of mine is overdue',
  revision_requested: 'A revision is requested',
  file_submitted_for_review: 'A file is submitted for review',
};

export function preferenceLabel(event: NotificationEvent): string {
  return SETTING_LABELS[event] ?? NOTIFICATION_EVENT_LABELS[event];
}

/**
 * The state of one toggle, given whatever rows came back.
 *
 * ABSENT MEANS ENABLED, matching slack_fanout_allowed() exactly. If the screen
 * defaulted a missing row to "off" it would show every person their whole list
 * switched off on first visit, they would assume they were already muted, and
 * the one place the truth lives — the database — would say the opposite.
 */
export function preferenceFor(
  rows: NotificationPreference[],
  event: NotificationEvent,
): NotificationPreference {
  return (
    rows.find((row) => row.event_type === event) ?? {
      event_type: event,
      slack_enabled: true,
      in_app_alert_enabled: true,
    }
  );
}

/** What the Slack column says when the whole channel is off for this person. */
export function slackSummary(rows: NotificationPreference[]): string {
  const off = PREFERENCE_EVENTS.filter((event) => !preferenceFor(rows, event).slack_enabled);
  if (off.length === 0) {
    return 'All events post to #tap-notif.';
  }
  if (off.length === PREFERENCE_EVENTS.length) {
    return 'Nothing posts to #tap-notif. Everything still appears in your Inbox.';
  }
  return `${String(off.length)} of ${String(PREFERENCE_EVENTS.length)} events do not post to #tap-notif.`;
}

// ── Workspace settings ─────────────────────────────────────────────────────

export interface WorkspaceSettings {
  default_project_visibility: string;
  default_task_due_days: number;
  default_sample_due_days: number;
  require_due_date_on_tasks: boolean;
  allow_member_project_creation: boolean;
}

/**
 * What is wrong with a proposed workspace value, or null.
 *
 * The same bounds the database checks (workspace_settings_due_days_sane). Said
 * here so somebody is told while typing rather than by a constraint violation
 * after pressing save — and only here as a courtesy: the database is what
 * actually refuses it.
 */
export function workspaceProblem(settings: Partial<WorkspaceSettings>): string | null {
  const due = settings.default_task_due_days;
  if (due !== undefined && (!Number.isInteger(due) || due < 0 || due > 365)) {
    return 'Default task due days must be a whole number between 0 and 365.';
  }
  const sample = settings.default_sample_due_days;
  if (sample !== undefined && (!Number.isInteger(sample) || sample < 1 || sample > 365)) {
    return 'Default sample due days must be a whole number between 1 and 365.';
  }
  return null;
}

// ── Time zones ─────────────────────────────────────────────────────────────

/**
 * The zones offered in the picker.
 *
 * Read from the platform rather than hard-coded: Intl ships the current IANA
 * list and it changes a few times a year. The fallback list is short and
 * covers where this team actually is — a picker that renders nothing is worse
 * than one that renders eight rows.
 */
export function timeZoneOptions(): string[] {
  const supported = (Intl as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  if (typeof supported === 'function') {
    try {
      const zones = supported('timeZone');
      // UTC is prepended because the IANA list does NOT contain it — it spells
      // that zone 'Etc/UTC'. profiles.time_zone defaults to 'UTC', so without
      // this every person who has never changed it has a stored value absent
      // from the picker, and the select renders with nothing chosen.
      return ['UTC', ...zones.filter((zone) => zone !== 'UTC')];
    } catch {
      // Fall through to the short list.
    }
  }
  return [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Asia/Manila',
    'Asia/Shanghai',
  ];
}

/** Whether a zone string is one the platform will accept. */
export function isKnownTimeZone(zone: string): boolean {
  if (zone.trim() === '') {
    return false;
  }
  try {
    // Throws RangeError on an unknown zone. Cheaper and more current than
    // matching against a list this file would have to maintain.
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}
